import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { splitTextIntoLines } from '@src/utils';

import { useClosedCaptionsStore } from '@src/stores';
import { useRoomContext } from '@src/contexts';

const useClosedCaptions = () => {
  const [containerWidth, setContainerWidth] = useState<number>(0);

  const containerRef = useRef<HTMLUListElement>(null);

  const { currentPeerId, handleUpdatePeerInfo } = useRoomContext();

  const { speakers, selectedLanguage, closedCaptionsError } = useClosedCaptionsStore(
    useShallow((state) => ({
      speakers: state.speakers,
      selectedLanguage: state.selectedLanguage,
      closedCaptionsError: state.closedCaptionsError,
    }))
  );

  const onChangeLanguage = useCallback(
    (selectedLanguage: string) => {
      useClosedCaptionsStore.setState({ selectedLanguage });
      handleUpdatePeerInfo({
        peer_id: currentPeerId,
        status: true,
        type: 'speechToText',
        emit: true,
        extraFields: {
          lang: selectedLanguage,
        },
      });
    },
    [currentPeerId, handleUpdatePeerInfo]
  );

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const containerElement = containerRef.current;

    const resizeObserver = new ResizeObserver(() => {
      const width = containerElement.getBoundingClientRect().width;
      setContainerWidth(width);
    });

    if (containerElement) {
      resizeObserver.observe(containerElement);
    }
    return () => {
      if (containerElement) {
        resizeObserver.unobserve(containerElement);
      }
    };
  }, []);

  const processedSpeakers = useMemo(
    () =>
      speakers.map((speaker) => {
        const lines = splitTextIntoLines(speaker.rawText, containerWidth).slice(-2);
        return { ...speaker, lines };
      }),
    [containerWidth, speakers]
  );

  return { speakers: processedSpeakers, containerRef, selectedLanguage, closedCaptionsError, onChangeLanguage };
};

export default useClosedCaptions;
